/**
 * Small session state of the onboarding feature (W2-14 onboarding-wizard): whether the tour and the Help panel are
 * open (for `tour.stop`, F1 toggling the panel and the `tour.active` when-atom), whether focus is inside the Help
 * panel (the `help.focused` when-atom: the panel is a non-modal sheet, i.e. a `role="dialog"` region, inside which
 * the keyboard router only applies bindings that require `dialog`, so F1 needs a binding of its own there to close
 * it) and a "Show me" request made before the Help panel opened (`help.showMe` from the palette opens the panel at
 * the topic, which then shows the part).
 */

import type { Unsubscribe } from '../../core/util/types';

export interface Flag {
  get(): boolean;
  set(on: boolean): void;
  subscribe(cb: () => void): Unsubscribe;
}

export function createFlag(initial = false): Flag {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(on) {
      if (on === value) return;
      value = on;
      for (const cb of listeners) cb();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

/** The tour is running. */
export const tourActive: Flag = createFlag(false);
/** The Help panel is open. */
export const helpOpen: Flag = createFlag(false);

let helpElement: Element | null = null;

/**
 * The Help panel registers an element inside it while it is shown; the returned function unregisters it (only if no
 * newer panel registered meanwhile).
 */
export function registerHelpElement(el: Element): () => void {
  helpElement = el;
  return () => {
    if (helpElement === el) helpElement = null;
  };
}

/** Is keyboard focus inside the open Help panel (its sheet, including its header and footer)? */
export function helpHasFocus(doc: Document | null = typeof document === 'undefined' ? null : document): boolean {
  const el = helpElement;
  if (el === null || doc === null || !el.isConnected) return false;
  const active = doc.activeElement;
  if (active === null) return false;
  const sheet = el.closest('[role="dialog"]') ?? el;
  return sheet.contains(active);
}

let pendingShowMe: string | null = null;

/** Asks the Help panel to run "Show me" for `topic` once it shows that topic. */
export function requestShowMe(topic: string): void {
  pendingShowMe = topic;
}

/** True (once) when "Show me" was requested for `topic`. */
export function takeShowMe(topic: string): boolean {
  if (pendingShowMe !== topic) return false;
  pendingShowMe = null;
  return true;
}
